/**
 * VoicemailPanel — fetches `/api/integrations/ringcentral/voicemails` and lets
 * the rep play each voicemail inline. Audio playback uses the token-aware
 * Bearer fetch → blob → object URL approach so we never expose the JWT to the
 * <audio> element URL.
 *
 * Extract-actions: when a voicemail has a transcription, the rep can press
 * "Extract actions" → we resolve the caller phone → lead via
 * `/api/integrations/rc/match-inbound`, then POST the transcript to
 * `/api/leads/{lead_id}/extract-actions`. The endpoint persists the
 * generated CRM tasks server-side and returns the count, which we surface
 * via a sonner toast linking back to the lead's Tasks tab.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { API, useAuth } from "@/App";
import { Loader2, Play, RefreshCw, Voicemail, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

async function authedFetch(url, token, init = {}) {
  return fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

function ExtractActionsButton({ vm, token }) {
  const [state, setState] = useState("idle"); // idle | extracting | done | error
  const [extracted, setExtracted] = useState(null); // { lead_id, task_count, cached }

  const hasTranscript = !!(vm.transcription && vm.transcription.trim());

  const onClick = useCallback(async () => {
    if (!hasTranscript || state === "extracting") return;
    setState("extracting");
    try {
      // 1) Resolve caller phone → lead
      const phone = vm.from_number || "";
      if (!phone) throw new Error("Voicemail has no caller phone — can't match a lead.");
      const matchResp = await authedFetch(
        `${API}/integrations/rc/match-inbound?phone=${encodeURIComponent(phone)}`,
        token,
      );
      if (!matchResp.ok) throw new Error(`Phone match failed (${matchResp.status})`);
      const matchData = await matchResp.json();
      const leadId = matchData?.match?.lead_id;
      if (!leadId) {
        if (matchData?.ambiguous) {
          throw new Error("Multiple leads share this phone — open the lead manually to extract.");
        }
        throw new Error("No CRM lead is linked to this caller number yet.");
      }

      // 2) Send transcript to the action-extractor endpoint
      const extractResp = await authedFetch(
        `${API}/leads/${leadId}/extract-actions`,
        token,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            transcript: vm.transcription,
            source: "voicemail",
            source_id: vm.id,
          }),
        },
      );
      if (!extractResp.ok) {
        const text = await extractResp.text();
        throw new Error(text.slice(0, 200) || `Extract failed (${extractResp.status})`);
      }
      const payload = await extractResp.json();
      const taskCount = Array.isArray(payload.tasks) ? payload.tasks.length : 0;
      const cached = !!payload.cached;

      setExtracted({ lead_id: leadId, task_count: taskCount, cached });
      setState("done");
      toast.success(
        cached
          ? `Already extracted — ${taskCount} task${taskCount === 1 ? "" : "s"} on this lead.`
          : taskCount > 0
            ? `Created ${taskCount} task${taskCount === 1 ? "" : "s"} from this voicemail.`
            : "No actionable items detected in this voicemail.",
        {
          action: leadId
            ? {
                label: "Open lead",
                onClick: () => window.open(`/lead-focus/${leadId}`, "_blank"),
              }
            : undefined,
        },
      );
    } catch (e) {
      setState("error");
      toast.error(String(e?.message || e));
    }
  }, [vm.id, vm.from_number, vm.transcription, hasTranscript, token, state]);

  if (!hasTranscript) return null;

  return (
    <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
      <button
        data-testid={`vm-extract-${vm.id}`}
        onClick={onClick}
        disabled={state === "extracting"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9,
          color: state === "error" ? "var(--danger)" : "var(--purple, #6e36c9)",
          background: "none",
          border: `0.5px solid ${state === "error" ? "var(--danger)" : "var(--purple, #6e36c9)"}`,
          borderRadius: 6, padding: "2px 6px",
          cursor: state === "extracting" ? "wait" : "pointer",
          fontWeight: 600, letterSpacing: 0.2,
        }}
      >
        {state === "extracting" ? (
          <><Loader2 style={{ width: 9, height: 9, animation: "spin 1s linear infinite" }} /> Extracting…</>
        ) : state === "error" ? (
          <>Retry extract</>
        ) : (
          <><Sparkles style={{ width: 9, height: 9 }} /> Extract actions</>
        )}
      </button>
      {state === "done" && extracted && (
        <a
          data-testid={`vm-extract-result-${vm.id}`}
          href={`/lead-focus/${extracted.lead_id}`}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9,
            color: "var(--text-2)", textDecoration: "none",
          }}
        >
          {extracted.cached ? "Already extracted" : `${extracted.task_count} task${extracted.task_count === 1 ? "" : "s"} created`}
          <ExternalLink style={{ width: 8, height: 8 }} />
        </a>
      )}
    </div>
  );
}

function VoicemailRow({ vm, token }) {
  const [state, setState] = useState("idle");
  const [audioUrl, setAudioUrl] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => () => {
    if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch { /* ignore */ } }
  }, []);

  const load = useCallback(async () => {
    if (state === "loading" || state === "ready") return;
    setState("loading");
    try {
      const resp = await fetch(`${API}/integrations/ringcentral/voicemail/${vm.id}/audio`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setAudioUrl(url);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [vm.id, token, state]);

  const at = vm.creation_time
    ? new Date(vm.creation_time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "—";

  return (
    <div
      data-testid={`voicemail-row-${vm.id}`}
      style={{
        display: "grid", gridTemplateColumns: "auto 1fr auto",
        gap: 8, alignItems: "center", padding: "10px 12px",
        borderBottom: "0.5px solid var(--border)",
      }}
    >
      <div style={{
        width: 26, height: 26, borderRadius: "50%",
        background: vm.read_status === "Read" ? "var(--gray-light)" : "var(--blue-light)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Voicemail style={{ width: 12, height: 12, color: vm.read_status === "Read" ? "var(--text-tertiary)" : "var(--blue)" }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-primary)" }}>
          {vm.from_number || "Unknown caller"}
        </div>
        <div style={{ fontSize: 9, color: "var(--text-tertiary)", marginTop: 2 }}>
          {at}{vm.duration ? ` · ${vm.duration}s` : ""}
        </div>
        {vm.transcription && (
          <div style={{ fontSize: 10, color: "var(--text-2)", marginTop: 3, lineHeight: 1.35 }}>
            {vm.transcription}
          </div>
        )}
        <ExtractActionsButton vm={vm} token={token} />
      </div>
      <div>
        {state === "ready" && audioUrl ? (
          <audio data-testid={`vm-audio-${vm.id}`} controls src={audioUrl} style={{ height: 26, maxWidth: 180 }} />
        ) : (
          <button
            data-testid={`vm-play-${vm.id}`}
            onClick={load}
            disabled={!vm.attachment_id || state === "loading"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10,
              color: state === "error" ? "var(--danger)" : "var(--blue)",
              background: "none", border: "0.5px solid var(--border)",
              borderRadius: 6, padding: "3px 8px",
              cursor: vm.attachment_id && state !== "loading" ? "pointer" : "not-allowed",
              opacity: vm.attachment_id ? 1 : 0.4,
            }}
          >
            {state === "loading"
              ? <><Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> …</>
              : state === "error"
                ? <>Retry</>
                : <><Play style={{ width: 10, height: 10 }} /> Play</>}
          </button>
        )}
      </div>
    </div>
  );
}

export default function VoicemailPanel({ days = 7 }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API}/integrations/ringcentral/voicemails?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t.slice(0, 200) || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setItems(Array.isArray(data.voicemails) ? data.voicemails : []);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="voicemail-panel" style={{ maxHeight: 320, overflowY: "auto" }}>
      <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", borderBottom: "0.5px solid var(--border)" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-2)" }}>
          Voicemails · last {days} days
        </span>
        <button
          data-testid="vm-refresh"
          onClick={load}
          disabled={loading}
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--blue)", background: "none", border: "none", cursor: loading ? "wait" : "pointer", padding: 0 }}
        >
          <RefreshCw style={{ width: 10, height: 10, animation: loading ? "spin 1s linear infinite" : "none" }} /> Refresh
        </button>
      </div>
      {loading && items.length === 0 ? (
        <div data-testid="vm-loading" style={{ padding: "28px 12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
          <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 4 }} /> Loading voicemails…
        </div>
      ) : error ? (
        <div data-testid="vm-error" style={{ padding: "16px 12px", color: "var(--danger)", fontSize: 11, background: "var(--danger-soft)" }}>
          {error}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="vm-empty" style={{ padding: "28px 12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
          No voicemails in the last {days} days.
        </div>
      ) : (
        items.map((vm) => <VoicemailRow key={vm.id} vm={vm} token={token} />)
      )}
    </div>
  );
}
